import axios from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import {
    FETCH_DRIVE_FILES_REQUEST,
    FETCH_DRIVE_FILES_SUCCESS,
    FETCH_DRIVE_FILES_FAILURE,
    SYNC_DRIVE_FILES_REQUEST,
    SYNC_DRIVE_FILES_SUCCESS,
    SYNC_DRIVE_FILES_FAILURE,
    CREATE_DRIVE_FOLDER_REQUEST,
    CREATE_DRIVE_FOLDER_SUCCESS,
    CREATE_DRIVE_FOLDER_FAILURE
} from '../constants/driveFiles.constant';
import { BACKEND_API_URL } from "../../utils/api";

const REACT_APP_BACKEND_API_URL = BACKEND_API_URL;

export const fetchDriveFilesRequest = (folderId, requestId) => ({
    type: FETCH_DRIVE_FILES_REQUEST,
    payload: { folderId, requestId }
});

export const fetchDriveFilesSuccess = (folderId, data, requestId) => ({
    type: FETCH_DRIVE_FILES_SUCCESS,
    payload: { folderId, data, requestId }
});

export const fetchDriveFilesFailure = (folderId, error, requestId) => ({
    type: FETCH_DRIVE_FILES_FAILURE,
    payload: { folderId, error, requestId }
});

export const syncDriveFilesRequest = (folderId) => ({
    type: SYNC_DRIVE_FILES_REQUEST,
    payload: { folderId }
});

export const syncDriveFilesSuccess = (folderId, data) => ({
    type: SYNC_DRIVE_FILES_SUCCESS,
    payload: { folderId, data }
});

export const syncDriveFilesFailure = (folderId, error) => ({
    type: SYNC_DRIVE_FILES_FAILURE,
    payload: { folderId, error }
});

export const createDriveFolderRequest = (folderId) => ({
    type: CREATE_DRIVE_FOLDER_REQUEST,
    payload: folderId
});

export const createDriveFolderSuccess = (folderId, gdriveFolderId) => ({
    type: CREATE_DRIVE_FOLDER_SUCCESS,
    payload: { folderId, gdriveFolderId }
});

export const createDriveFolderFailure = (folderId, error) => ({
    type: CREATE_DRIVE_FOLDER_FAILURE,
    payload: { folderId, error }
});

// Thunk to fetch Drive files from backend
// sync: false only reads the DB copy, without triggering a Google Drive sync afterwards
export const fetchDriveFiles = (folderId, { sync = true } = {}) => async (dispatch) => {
    const requestId = Date.now();
    dispatch(fetchDriveFilesRequest(folderId, requestId));
    
    try {
        const accessToken = Cookies.get('access_token');
        const response = await axios.get(
            `${REACT_APP_BACKEND_API_URL}/api/folder/${folderId}/drive_files/`,
            {
                headers: {
                    Authorization: `Token ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            }
        );

        const data = {
            driveFiles: response.data.drive_files || [],
            gdriveFolderId: response.data.gdrive_folder_id,
            lastSyncedAt: response.data.gdrive_last_synced_at
        };

    dispatch(fetchDriveFilesSuccess(folderId, data, requestId));

        // Trigger async sync if folder has Drive ID (sync responses will also be applied if current)
        if (sync && response.data.gdrive_folder_id) {
            dispatch(syncDriveFiles(folderId));
        }

        return data;
    } catch (error) {
        console.error('Failed to fetch drive data:', error);
        dispatch(fetchDriveFilesFailure(folderId, error.message, requestId));
        throw error;
    }
};

// Thunk to sync Drive files from Google Drive
// Backend skips the Google Drive call if the folder was synced recently; force: true (manual refresh) bypasses that
export const syncDriveFiles = (folderId, { force = false } = {}) => async (dispatch) => {
    dispatch(syncDriveFilesRequest(folderId));
    
    try {
        const accessToken = Cookies.get('access_token');
        const response = await axios.post(
            `${REACT_APP_BACKEND_API_URL}/api/folder/${folderId}/sync_drive_files/`,
            { force },
            {
                headers: {
                    Authorization: `Token ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            }
        );

        const data = {
            driveFiles: response.data.drive_files,
            lastSyncedAt: response.data.gdrive_last_synced_at
        };

        // include requestId on sync success so reducer logic (if added) can inspect freshness if needed
        dispatch(syncDriveFilesSuccess(folderId, data));
        return data;
    } catch (error) {
        console.error('Drive sync failed:', error);
        dispatch(syncDriveFilesFailure(folderId, error.message));
        throw error;
    }
};

// Thunk to create Drive folder
export const createDriveFolder = (folderId) => async (dispatch) => {
    dispatch(createDriveFolderRequest(folderId));
    
    try {
        const accessToken = Cookies.get('access_token');
        const response = await axios.post(
            `${REACT_APP_BACKEND_API_URL}/api/folder/${folderId}/create_gdrive_folder/`,
            {},
            {
                headers: {
                    Authorization: `Token ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            }
        );

        dispatch(createDriveFolderSuccess(folderId, response.data.gdrive_folder_id));
        toast.success('Google Drive folder created!');
        
        // Fetch updated data
        dispatch(fetchDriveFiles(folderId));
        
        return response.data.gdrive_folder_id;
    } catch (error) {
        console.error('Drive folder creation failed:', error);
        toast.error(error.response?.data?.error || 'Failed to create Drive folder');
        dispatch(createDriveFolderFailure(folderId, error.message));
        throw error;
    }
};

// Smart thunk that fetches if no data exists, otherwise just syncs
// sync: false only loads the DB copy when nothing is cached yet (used for ancestor columns)
export const fetchOrSyncDriveFiles = (folderId, { sync = true } = {}) => async (dispatch, getState) => {
    const state = getState();
    const existingData = state.driveFiles.byFolderId[folderId];
    
    // If no data exists, do a full fetch (which will also trigger sync)
    if (!existingData || !existingData.gdriveFolderId) {
        return dispatch(fetchDriveFiles(folderId, { sync }));
    }
    
    if (!sync) {
        return existingData;
    }

    // If data exists, just sync from Google Drive
    return dispatch(syncDriveFiles(folderId));
};

